// src/pages/settings/CompanyProfileTab.jsx
import React, { useEffect, useState, useCallback } from 'react';
import axios from '../../api/axios';
import { toast } from 'react-toastify';

const TIMEZONE_OPTIONS = [
  { value: 'America/Vancouver', label: 'Pacific – Vancouver (PT)' },
  { value: 'America/Los_Angeles', label: 'Pacific – Los Angeles (PT)' },
  { value: 'America/Denver', label: 'Mountain – Denver (MT)' },
  { value: 'America/Chicago', label: 'Central – Chicago (CT)' },
  { value: 'America/Toronto', label: 'Eastern – Toronto (ET)' },
  { value: 'America/New_York', label: 'Eastern – New York (ET)' },
  { value: 'UTC', label: 'UTC' },
];

const CURRENCY_OPTIONS = [
  { value: 'CAD', label: 'CAD – Canadian Dollar' },
  { value: 'USD', label: 'USD – US Dollar' },
];

// ⚠️ Must stay in sync with backend / Stripe Connect allowlist
const COUNTRY_OPTIONS = [
  { value: 'CA', label: 'Canada' },
  { value: 'US', label: 'United States' },
];

const CompanyProfileTab = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ---- load profile ----
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/company/profile');
      const data = res?.data || {};

      // Shape from backend:
      // {
      //   name, industry, website, email, phone, address,
      //   timezone, currency, locale,
      //   plan, billingStatus,
      //   billingCountry?
      // }
      setProfile({
        name: data.name || '',
        industry: data.industry || '',
        website: data.website || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        plan: data.plan || null,
        billingStatus: data.billingStatus || null,
        timezone: data.timezone || 'UTC',
        currency: data.currency || 'CAD',
        locale: data.locale || 'en-US',
        billingCountry: data.billingCountry || '', // NEW
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load company profile');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Simple field updater (only top-level fields)
  const updateField = (field, value) => {
    setProfile((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  // ---- save profile ----
  const handleSave = async () => {
    if (!profile) return;

    // Only send the fields the controller actually uses
    const payload = {
      name: profile.name || '',
      industry: profile.industry || '',
      website: profile.website || '',
      email: profile.email || '',
      phone: profile.phone || '',
      address: profile.address || '',
      // NEW: company-wide preferences
      timezone: profile.timezone || 'UTC',
      currency: profile.currency || 'CAD',
      locale: profile.locale || 'en-US',
      // NEW: billing country (for Stripe Connect, tax, etc.)
      billingCountry: profile.billingCountry || undefined,
      // NOTE: we are NOT sending plan/billingStatus/etc from here
    };

    try {
      setSaving(true);
      await axios.patch('/company/profile', payload);
      toast.success('Company profile updated');
      // reload to ensure we show what backend persisted
      fetchProfile();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update company profile');
    } finally {
      setSaving(false);
    }
  };

  const labelCls = 'block text-xs font-medium text-slate-700';
  const inputCls =
    'mt-1 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500';
  const textareaCls =
    'mt-1 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500';

  if (loading && !profile) {
    return (
      <div className="mx-auto max-w-4xl rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 w-48 rounded bg-slate-200" />
          <div className="h-4 w-full rounded bg-slate-200" />
          <div className="h-4 w-5/6 rounded bg-slate-200" />
          <div className="h-4 w-3/4 rounded bg-slate-200" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="mx-auto max-w-3xl rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800">
        Failed to load company profile.
      </div>
    );
  }

  const planLabel =
    profile.plan === 'starter'
      ? 'Starter'
      : profile.plan === 'pro'
      ? 'Pro'
      : profile.plan === 'advanced'
      ? 'Advanced'
      : profile.plan || null;

  const billingStatus = profile.billingStatus
    ? String(profile.billingStatus).toLowerCase()
    : null;

  const billingStatusLabel = billingStatus
    ? billingStatus.replace('_', ' ')
    : null;

  const billingStatusCls =
    billingStatus === 'active'
      ? 'bg-green-50 text-green-700 ring-green-200'
      : billingStatus === 'trialing'
      ? 'bg-blue-50 text-blue-700 ring-blue-200'
      : billingStatus === 'past_due'
      ? 'bg-amber-50 text-amber-700 ring-amber-200'
      : billingStatus === 'canceled'
      ? 'bg-slate-100 text-slate-600 ring-slate-200'
      : 'bg-slate-50 text-slate-600 ring-slate-200';

  return (
    <div className="space-y-4">
      {/* Header (single, not nested) */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            Workspace identity
          </h1>
          <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
            This is the information we use on invoices and across your workspace.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {planLabel && (
            <span className="inline-flex items-center rounded-full bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-700 ring-1 ring-inset ring-slate-200">
              Plan: <span className="ml-1 font-semibold">{planLabel}</span>
            </span>
          )}

          {billingStatusLabel && (
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium ring-1 ring-inset ${billingStatusCls}`}
            >
              Billing: {billingStatusLabel}
            </span>
          )}

          <button
            type="button"
            onClick={fetchProfile}
            disabled={saving}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* Single, subtle card (no extra big header) */}
      <section className="mx-auto w-full max-w-4xl rounded-2xl border border-slate-200 bg-white/95 p-5 sm:p-6 shadow-sm">
        <div className="mb-4 space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Profile details
          </p>
          <p className="text-xs text-slate-500">
            These fields are used on new invoices and across the app. Old
            invoices keep their original snapshot.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Company name</label>
              <input
                type="text"
                className={inputCls}
                value={profile.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="e.g. Bob's HVAC"
              />
            </div>

            <div>
              <label className={labelCls}>Industry</label>
              <input
                type="text"
                className={inputCls}
                value={profile.industry}
                onChange={(e) => updateField('industry', e.target.value)}
                placeholder="e.g. HVAC, Cleaning"
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Website</label>
            <input
              type="url"
              className={inputCls}
              value={profile.website}
              onChange={(e) => updateField('website', e.target.value)}
              placeholder="https://example.com"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Primary email</label>
              <input
                type="email"
                className={inputCls}
                value={profile.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="hello@company.com"
              />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input
                type="tel"
                className={inputCls}
                value={profile.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder="+1 (555) 555-5555"
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Address</label>
            <textarea
              rows={2}
              className={textareaCls}
              value={profile.address}
              onChange={(e) => updateField('address', e.target.value)}
              placeholder="123 Main St, Toronto, ON, Canada"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              This address is used as the seller address on new invoices.
            </p>
          </div>

          {/* NEW: Regional & formatting */}
          <div className="mt-5 border-t border-slate-100 pt-4 sm:pt-5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Regional & formatting
            </p>
            <p className="mt-1 text-xs text-slate-500">
              These defaults are used for dates, times and money across your workspace.
            </p>

            <div className="mt-3 grid gap-4 sm:mt-4 sm:grid-cols-4">
              <div className="sm:col-span-2">
                <label className={labelCls}>Timezone</label>
                <select
                  className={inputCls}
                  value={profile.timezone || 'UTC'}
                  onChange={(e) => updateField('timezone', e.target.value)}
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Currency</label>
                <select
                  className={inputCls}
                  value={profile.currency || 'CAD'}
                  onChange={(e) => updateField('currency', e.target.value)}
                >
                  {CURRENCY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Locale (advanced)</label>
                <input
                  type="text"
                  className={inputCls}
                  value={profile.locale || 'en-US'}
                  onChange={(e) => updateField('locale', e.target.value)}
                  placeholder="en-US"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  Optional. Controls number/date formatting in future updates.
                </p>
              </div>
            </div>

            {/* NEW: Business / billing country */}
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div className="sm:col-span-1">
                <label className={labelCls}>Business / billing country</label>
                <select
                  className={inputCls}
                  value={profile.billingCountry || ''}
                  onChange={(e) => updateField('billingCountry', e.target.value)}
                >
                  <option value="">Select country</option>
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  Used for Stripe Connect and tax/regional defaults. For now we support
                  Canada and the United States for online payments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyProfileTab;
